import { useMemo, useSyncExternalStore } from "react";
import {
  type Company,
  type DeliveryPoint,
  type Site,
} from "@syllarys/contracts";
import { useEms } from "../state/EmsContext";
import { contextualSiteStatus } from "../domain/dataConfidence";
import { resolvePortfolioSelection } from "../domain/portfolioSelection";

interface PortfolioScope {
  companies: Company[];
  sites: Site[];
  deliveryPoints: DeliveryPoint[];
  selectedCompany: Company | null;
  selectedSite: Site | null;
  selectedPoint: DeliveryPoint | null;
}

export function usePortfolio(): PortfolioScope {
  const { db, session } = useEms();
  const selectionKey = useSyncExternalStore(
    subscribeToPortfolioSelection,
    readPortfolioSelection,
    () => "",
  );
  return useMemo(() => {
    if (!session)
      return {
        companies: [],
        sites: [],
        deliveryPoints: [],
        selectedCompany: null,
        selectedSite: null,
        selectedPoint: null,
      };
    const brandId = session.brand.id;
    const allCompanies = db.companies.filter(
      (item) =>
        item.brandId === brandId &&
        (item.lifecycleStatus ?? "active") === "active",
    );
    const isSuper = session.user.platformRole === "super_admin";
    const grants = session.activeMembership.grants;
    const hasBrandGrant = grants.some(
      (grant) => grant.scope === "brand" && grant.scopeId === brandId,
    );
    const grantedSiteIds = new Set(
      grants.flatMap((grant) => {
        if (grant.scope === "site") return [grant.scopeId];
        if (grant.scope !== "delivery_point") return [];
        const point = db.deliveryPoints.find(
          (item) => item.id === grant.scopeId && item.brandId === brandId,
        );
        return point ? [point.siteId] : [];
      }),
    );
    const companies = isSuper
      ? allCompanies
      : hasBrandGrant
        ? allCompanies
        : allCompanies.filter(
            (company) =>
              (session.activeMembership.role === "manager" &&
                company.createdByMembershipId ===
                  session.activeMembership.id) ||
              db.sites.some(
                (site) =>
                  site.brandId === brandId &&
                  site.companyId === company.id &&
                  grantedSiteIds.has(site.id),
              ),
          );
    const sites = db.sites
      .filter(
        (site) =>
          site.brandId === brandId &&
          (site.lifecycleStatus ?? "active") === "active" &&
          companies.some((company) => company.id === site.companyId) &&
          (isSuper || hasBrandGrant || grantedSiteIds.has(site.id)),
      )
      .map((site) => ({
        ...site,
        status: contextualSiteStatus(site, db.deliveryPoints),
      }));
    const deliveryPoints = db.deliveryPoints.filter((point) => {
      if (point.status === "inactive") return false;
      const site = db.sites.find((item) => item.id === point.siteId);
      return Boolean(
        site &&
        sites.some((item) => item.id === site.id) &&
        (isSuper ||
          hasBrandGrant ||
          grants.some(
            (grant) =>
              (grant.scope === "site" && grant.scopeId === site.id) ||
              (grant.scope === "delivery_point" && grant.scopeId === point.id),
          )),
      );
    });
    const { selectedCompany, selectedSite, selectedPoint } =
      resolvePortfolioSelection(companies, sites, deliveryPoints, selectionKey);
    return {
      companies,
      sites,
      deliveryPoints,
      selectedCompany,
      selectedSite,
      selectedPoint,
    };
  }, [db, selectionKey, session]);
}

const COMPANY_KEY = "syllarys-ems:v1:selected-company";
const SITE_KEY = "syllarys-ems:v1:selected-site";
const POINT_KEY = "syllarys-ems:v1:selected-point";
const LEGACY_KEYS = [
  [COMPANY_KEY, "phebus-selected-company"],
  [SITE_KEY, "phebus-selected-site"],
  [POINT_KEY, "phebus-selected-point"],
] as const;
const SELECTION_EVENT = "syllarys-ems:portfolio-selection-change";

function migrateSelectionKeys(): void {
  try {
    for (const [current, legacy] of LEGACY_KEYS) {
      if (localStorage.getItem(current) !== null) continue;
      const value = localStorage.getItem(legacy);
      if (value !== null) localStorage.setItem(current, value);
      localStorage.removeItem(legacy);
    }
  } catch {
    // Selection defaults remain available without browser storage.
  }
}

function readPortfolioSelection(): string {
  try {
    migrateSelectionKeys();
    return [COMPANY_KEY, SITE_KEY, POINT_KEY]
      .map((key) => localStorage.getItem(key) ?? "")
      .join("|");
  } catch {
    return "||";
  }
}

function subscribeToPortfolioSelection(onChange: () => void): () => void {
  window.addEventListener(SELECTION_EVENT, onChange);
  window.addEventListener("storage", onChange);
  return () => {
    window.removeEventListener(SELECTION_EVENT, onChange);
    window.removeEventListener("storage", onChange);
  };
}

function notifyPortfolioSelection(): void {
  window.dispatchEvent(new Event(SELECTION_EVENT));
}

export function selectCompany(companyId: string): void {
  try {
    localStorage.setItem(COMPANY_KEY, companyId);
    localStorage.removeItem(SITE_KEY);
    localStorage.removeItem(POINT_KEY);
  } catch {
    // Navigation still works from React state when storage is unavailable.
  }
  notifyPortfolioSelection();
}

export function selectSite(siteId: string): void {
  try {
    localStorage.setItem(SITE_KEY, siteId);
    localStorage.removeItem(POINT_KEY);
  } catch {
    // Navigation still works from React state when storage is unavailable.
  }
  notifyPortfolioSelection();
}

export function selectPoint(pointId: string): void {
  try {
    localStorage.setItem(POINT_KEY, pointId);
  } catch {
    // Navigation still works from React state when storage is unavailable.
  }
  notifyPortfolioSelection();
}
